import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import React, { FC, useEffect, useState } from 'react';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, IconButton } from '@mui/material';
import { Stack } from '@mui/material';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { companyAPI } from '../../../services/CompanyService';
import { isOwner } from '../../../store/reducers/AuthSlice';

interface IFormInputs {
  company_name: string;
  next_ticket_number: number;
}

const Content: FC = () => {
  const { t } = useTranslation();

  const {
    data: company,
    error,
    isLoading: isLoading_point,
  } = companyAPI.useGetCompanyQuery('');

  const [updateCompany, { isError, error: errorupdatePoint }] =
    companyAPI.useUpdateCompanyMutation();

  const isOwnerVal = useSelector(isOwner);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<IFormInputs>({
    defaultValues: {
      company_name: '',
      next_ticket_number: 0,
    },
  });

  useEffect(() => {
    if (company) {
      setValue('company_name', company.company_name);
      setValue('next_ticket_number', company.next_ticket_number);
    }
  }, [company]);

  const dataFromError =
    errorupdatePoint &&
    'data' in errorupdatePoint &&
    errorupdatePoint.data &&
    typeof errorupdatePoint.data === 'object' &&
    'message' in errorupdatePoint.data &&
    typeof errorupdatePoint.data.message === 'string'
      ? errorupdatePoint.data.message
      : undefined;

  const [showSuccessesBlock, setshowSuccessesBlock] = useState(false);

  const onSubmit: SubmitHandler<IFormInputs> = async (args) => {
    const { data } = (await updateCompany(args)) as { data: any };

    if (data) {
      setshowSuccessesBlock(true);

      setTimeout(() => {
        setshowSuccessesBlock(false);
      }, 3000);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h6" gutterBottom margin={4}>
        {t('company.company_settings')}
      </Typography>

      <Stack spacing={3}>
        <Controller
          control={control}
          name="company_name"
          rules={{
            required: 'Name is required',
          }}
          render={({ field }) => {
            return (
              <TextField
                label={t('company.name') + '*'}
                {...field}
                error={!!errors.company_name}
                helperText={
                  errors.company_name?.message || t('printTicket.will_in_print')
                }
              />
            );
          }}
        />

        <Box
          component="fieldset"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            pl: 2,
            pr: 2,
            textAlign: 'left',
          }}
        >
          <Typography component="legend" sx={{ px: 1, fontSize: 12, mb: 0 }}>
            {t('company.tickets_settings')}
          </Typography>
          <Grid container spacing={1} direction="column" sx={{ pt: 1 }}>
            <Grid size={{ xs: 5, sm: 5 }} sx={{ mb: 1 }}>
              <Box>
                <Controller
                  control={control}
                  name="next_ticket_number"
                  render={({ field }) => (
                    <TextField
                      label={
                        <Box>
                          {t('company.next_ticket_number')}
                          <Tooltip
                            title={
                              <>{t('company.next_ticket_number_helper')}</>
                            }
                          >
                            <IconButton color="inherit">
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      {...field}
                      type="number"
                      variant="outlined"
                      size="small"
                      error={!!errors.next_ticket_number}
                      helperText={errors.next_ticket_number?.message}
                      slotProps={{
                        htmlInput: {
                          min: company?.next_ticket_number,
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {isError && <Typography color="error">{dataFromError}</Typography>}

        {showSuccessesBlock && (
          <Typography color="green">{t('updated')}</Typography>
        )}

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          disabled={!isOwnerVal}
        >
          {t('form.btn_save')}
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default Content;
